import { create } from 'zustand';
import { logger } from '@/lib/logger';
import type { IBackend } from '@/services/backend/IBackend';
import { useTerminalStore } from './terminalStore';
import { useRecentActivityStore, startRecentActivityTicker } from './recentActivityStore';

/**
 * Store for detecting when long-running terminal commands complete.
 *
 * Logic:
 * - Terminal output events increment an outputCount per project
 * - When output stops for 10s AND the busy period had sustained output
 *   (>= 20 data events over >= 15s) → fire completion
 * - This filters out shell prompts, short commands, and interactive typing
 * - After firing, state resets so the next burst starts fresh
 */

interface ProjectBusyState {
  lastOutputTime: number;
  busySince: number;
  outputCount: number; // Number of data events in this busy period
  isBusy: boolean;
}

type CompletionCallback = (projectPath: string) => void;

interface CompletionState {
  busyStates: Map<string, ProjectBusyState>;
  recordOutput: (projectPath: string) => void;
}

const IDLE_THRESHOLD_MS = 10_000;      // 10s silence → completion
const MIN_BUSY_DURATION_MS = 15_000;   // Must be busy for >15s
const MIN_OUTPUT_EVENTS = 20;          // Must have >=20 data events (filters short commands)
const BUSY_RESET_MS = 12_000;          // If gap between outputs >12s, treat as new busy period

// Prompt patterns are now handled daemon-side via the completion_pattern regex.
// The daemon sends PatternMatch events for both attached and detached sessions.

const completionCallbacks = new Set<CompletionCallback>();

export function onCompletion(callback: CompletionCallback): () => void {
  completionCallbacks.add(callback);
  return () => { completionCallbacks.delete(callback); };
}

export const useCompletionStore = create<CompletionState>((set) => ({
  busyStates: new Map(),

  recordOutput: (projectPath: string) => {
    const now = Date.now();
    set((state) => {
      const newStates = new Map(state.busyStates);
      const existing = newStates.get(projectPath);

      // If there was a long gap since last output, start a new busy period
      const gapTooLong = existing && (now - existing.lastOutputTime > BUSY_RESET_MS);

      if (!existing || !existing.isBusy || gapTooLong) {
        // Start fresh busy period
        newStates.set(projectPath, {
          lastOutputTime: now,
          busySince: now,
          outputCount: 1,
          isBusy: true,
        });
      } else {
        // Continue existing busy period
        newStates.set(projectPath, {
          ...existing,
          lastOutputTime: now,
          outputCount: existing.outputCount + 1,
        });
      }
      return { busyStates: newStates };
    });
  },
}));

// --- Completion check interval ---

let checkInterval: ReturnType<typeof setInterval> | null = null;

function checkForCompletions() {
  const now = Date.now();
  const { busyStates } = useCompletionStore.getState();

  busyStates.forEach((state, projectPath) => {
    if (!state.isBusy) return;
    const idleTime = now - state.lastOutputTime;
    const busyDuration = state.lastOutputTime - state.busySince;

    if (
      idleTime > IDLE_THRESHOLD_MS &&
      busyDuration > MIN_BUSY_DURATION_MS &&
      state.outputCount >= MIN_OUTPUT_EVENTS
    ) {
      logger.debug('CompletionStore', 'Completion detected for', projectPath,
        `(busy ${Math.round(busyDuration / 1000)}s, ${state.outputCount} events, idle ${Math.round(idleTime / 1000)}s)`);

      // Reset state completely so the next burst starts fresh
      useCompletionStore.setState((s) => {
        const newStates = new Map(s.busyStates);
        newStates.delete(projectPath);
        return { busyStates: newStates };
      });

      // Mark agent sessions for this project as 'completed'
      markProjectAgentsCompleted(projectPath);

      for (const cb of completionCallbacks) {
        try { cb(projectPath); } catch (e) {
          logger.error('CompletionStore', 'Completion callback error:', e);
        }
      }
    }
  });
}

/** Mark all working agent sessions in a project as 'completed', auto-clear after 5s. */
function markProjectAgentsCompleted(projectPath: string): void {
  const store = useTerminalStore.getState();
  const sessions = Array.from(store.sessions.values()).filter(
    (s) => s.projectPath === projectPath && s.isAgent && s.visualState === 'working',
  );
  for (const s of sessions) {
    store.updateVisualState(s.id, 'completed');
    // Auto-clear completed state after 5 seconds
    setTimeout(() => {
      const current = useTerminalStore.getState().sessions.get(s.id);
      if (current && current.visualState === 'completed') {
        useTerminalStore.getState().updateVisualState(s.id, 'idle');
      }
    }, 5000);
  }
}

// --- Terminal event subscription ---

let unsubTerminalData: (() => void) | null = null;
let unsubTerminalActivity: (() => void) | null = null;
let unsubPatternMatch: (() => void) | null = null;
const lastRecordTime = new Map<string, number>();

// Prompt pattern detection is now handled entirely by the PTY daemon
// via the completion_pattern regex. The daemon sends PatternMatch events
// for both attached and detached sessions. No frontend pattern matching needed.

/**
 * Handle terminal output for agent sessions.
 *
 * State machine is simple:
 *   idle/completed → working: when PatternMatch cooldown has expired
 *                             and we receive Data or Activity events
 *   working → completed → idle: driven ONLY by PatternMatch events
 *                                (daemon detects the prompt pattern)
 *
 * The daemon now checks the completion pattern for BOTH attached and
 * detached sessions, so PatternMatch is the single reliable signal
 * for "Claude is done."
 */

/** After a PatternMatch, ignore output for this many ms to let TUI settle. */
const PATTERN_MATCH_COOLDOWN_MS = 3000;
const patternMatchCooldowns = new Map<number, number>(); // sessionId → expiry

function handleTerminalOutput(id: number, _data?: string): void {
  const session = useTerminalStore.getState().sessions.get(id);
  if (!session) return;

  // Only track agent sessions
  if (!session.isAgent) return;

  // If within cooldown after PatternMatch (Claude just finished),
  // don't flip back to 'working' from TUI noise
  const cooldownExpiry = patternMatchCooldowns.get(id) ?? 0;
  if (Date.now() < cooldownExpiry) return;

  // Transition to 'working' if currently idle
  if (session.visualState !== 'working') {
    useTerminalStore.getState().updateVisualState(id, 'working');
  }

  // Feed the 5-minute footer-light tracker. Throttled with `lastRecordTime`
  // below — that throttle also covers this call so we don't spam set() at
  // every keystroke.
  const projectPath = session.projectPath;
  const now = Date.now();
  const last = lastRecordTime.get(projectPath) ?? 0;
  if (now - last < 500) return;
  lastRecordTime.set(projectPath, now);
  useRecentActivityStore.getState().recordActivity(projectPath);
  useCompletionStore.getState().recordOutput(projectPath);
}

export function startCompletionTracking(backend: IBackend): void {
  if (unsubTerminalData) return;

  // Track terminal-data events (attached agent terminals in the active project)
  unsubTerminalData = backend.terminal.onData((id: number, data: string) => {
    handleTerminalOutput(id, data);
  });

  // Track terminal-activity events (detached agent terminals in other projects)
  unsubTerminalActivity = backend.terminal.onActivity((id: number) => {
    handleTerminalOutput(id);
  });

  // Track pattern matches — the definitive "Claude is done" signal.
  // The daemon now sends PatternMatch for BOTH attached and detached sessions.
  unsubPatternMatch = backend.terminal.onPatternMatch((id: number) => {
    const session = useTerminalStore.getState().sessions.get(id);
    if (!session || !session.isAgent) return;

    logger.debug('CompletionStore', 'Pattern match: Claude done in session', id, 'project', session.projectPath);

    // Set cooldown so TUI noise doesn't flip back to 'working'
    patternMatchCooldowns.set(id, Date.now() + PATTERN_MATCH_COOLDOWN_MS);

    // Suppress the 5-minute footer light — Claude's prompt is back, the
    // agent is idle. ("Unless the last thing Claude said is that he's gone.")
    useRecentActivityStore.getState().recordCompletion(session.projectPath);

    // Clear busy state for this project
    useCompletionStore.setState((s) => {
      const newStates = new Map(s.busyStates);
      newStates.delete(session.projectPath);
      return { busyStates: newStates };
    });

    // Mark agent sessions as completed → auto-clears to idle after 5s
    markProjectAgentsCompleted(session.projectPath);

    for (const cb of completionCallbacks) {
      try { cb(session.projectPath); } catch (e) {
        logger.error('CompletionStore', 'Completion callback error:', e);
      }
    }
  });

  if (!checkInterval) {
    checkInterval = setInterval(checkForCompletions, 2000);
  }

  // Start the 30s tick that re-evaluates the 5-minute window so the footer
  // light naturally turns off even when no events are coming in.
  startRecentActivityTicker();

  logger.debug('CompletionStore', 'Completion tracking started');
}

export function stopCompletionTracking(): void {
  if (unsubTerminalData) {
    unsubTerminalData();
    unsubTerminalData = null;
  }
  if (unsubTerminalActivity) {
    unsubTerminalActivity();
    unsubTerminalActivity = null;
  }
  if (unsubPatternMatch) {
    unsubPatternMatch();
    unsubPatternMatch = null;
  }
  lastRecordTime.clear();
  if (checkInterval) {
    clearInterval(checkInterval);
    checkInterval = null;
  }
  logger.debug('CompletionStore', 'Completion tracking stopped');
}
